import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useChatSidebar } from '@/contexts/ChatSidebarContext';
import { 
  X, 
  MessageSquare, 
  Maximize2, 
  Minimize2,
  GripVertical,
  PanelRightClose,
  PanelRightOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AiChat } from '@/components/AiChat';
import { ChatHistory } from '@/components/chat/ChatHistory';
import { useChat } from '@/hooks/use-chat';

interface ChatSidebarGlobalProps {
  isOpen: boolean;
  onToggle: () => void;
  onResize?: (width: number) => void;
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 400;

export const ChatSidebarGlobal: React.FC<ChatSidebarGlobalProps> = ({
  isOpen,
  onToggle,
  onResize
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { startNewChat } = useChat();
  const { closeSidebar } = useChatSidebar();
  
  // Width management
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  
  // History sidebar state - default to closed
  const [showHistory, setShowHistory] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  
  // Auto-open on page load for desktop (only once)
  useEffect(() => {
    if (!isMobile && !isOpen && !hasAutoOpened) {
      onToggle();
      setHasAutoOpened(true);
    }
  }, [isMobile, isOpen, onToggle, hasAutoOpened]);
  
  // Handle close button click
  const handleClose = () => {
    closeSidebar();
  };
  
  // Drag to resize functionality
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);
  
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
    
    setWidth(clampedWidth);
    onResize?.(clampedWidth);
  }, [isResizing, onResize]);
  
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);
  
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);
  
  // Navigate to dedicated chat page
  const handleGoToFullChat = () => {
    navigate('/chat');
  };
  
  // Don't render on chat page
  if (location.pathname === '/chat') {
    return null;
  }
  
  // Don't render on mobile devices at all
  if (isMobile) {
    return null;
  }
  
  // Desktop: render as sidebar
  return (
    <>
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={cn(
          "fixed right-0 top-0 h-full bg-gradient-to-b from-slate-900 via-slate-900 to-black border-l border-white/10 shadow-2xl z-[95] transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{ width: `${width}px` }}
      >
        {/* Resize handle */}
        <div
          ref={dragRef}
          className="absolute left-0 top-0 w-1 h-full bg-transparent hover:bg-indigo-500/50 cursor-col-resize group z-10"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4 text-indigo-400" />
          </div>
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-slate-800/50 to-slate-900/50 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">Chat Assistant</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="text-white/70 hover:text-white"
              title={showHistory ? "Hide History" : "Show History"}
            >
              {showHistory ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-white/70 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex h-[calc(100vh-64px)]">
          {/* History panel (if enabled) */}
          {showHistory && (
            <div className="w-56 border-r border-white/10 bg-gradient-to-b from-slate-800/30 to-black/50 overflow-hidden">
              <div className="h-full">
                <ChatHistory />
              </div>
            </div>
          )}
          
          {/* Chat panel */}
          <div className="flex-1 min-w-0">
            <AiChat />
          </div>
        </div>
      </div>
    </>
  );
}; 